import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Paperclip, Send, X } from 'lucide-react';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENTS } from '@/lib/milliData';
import useDictation from '@/hooks/useDictation';

/* The draft survives (2026-10-13, J.O.: a long message was wiped when the
 * page timed out). The composer text lives in localStorage per room, written
 * on every change and removed on a successful send, so a tab change (the
 * component unmounts on the phone), a reload or a sign-out hands the words
 * back. Attachments are files and are not kept. */
const draftKey = (sessionId) => `kc-milli-draft:${sessionId || 'new'}`;
const readDraft = (sessionId) => {
  try { return localStorage.getItem(draftKey(sessionId)) || ''; } catch { return ''; }
};
const writeDraft = (sessionId, text) => {
  try {
    if (text && text.trim()) localStorage.setItem(draftKey(sessionId), text);
    else localStorage.removeItem(draftKey(sessionId));
  } catch { /* storage blocked or full: the box still works, the draft just does not survive */ }
};

/* The conversation itself: transcript on top, composer below. Milli's
 * bubbles are labelled with the desk that answered and wear its color;
 * the composer takes typing, photos and PDFs, and dictation where the
 * browser can listen. */

export default function ConsoleChat({ chat, t }) {
  const { i18n } = useTranslation();
  const {
    messages, thinking, historyLoading, send,
    attachments, attaching, armPicker, pickFiles, removeAttachment,
    sharedText, takeSharedText, sessionId,
  } = chat;
  const [input, setInput] = useState(() => readDraft(sessionId));
  const draftRoomRef = useRef(sessionId);   // the room the box currently holds a draft for
  const skipPersistRef = useRef(false);     // one render after a room switch, do not write the old words under the new key
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const dictation = useDictation({
    language: i18n.language,
    onText: (text) => {
      if (!text) return;
      setInput((cur) => (cur ? `${cur.replace(/\s+$/, '')} ${text}` : text));
    },
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking]);

  // Room switch: keep what was typed for the room we are leaving, bring up
  // whatever was typed for the room we are entering.
  useEffect(() => {
    if (draftRoomRef.current === sessionId) return;
    setInput((cur) => {
      writeDraft(draftRoomRef.current, cur);
      return readDraft(sessionId);
    });
    draftRoomRef.current = sessionId;
    skipPersistRef.current = true;
  }, [sessionId]);

  // Every keystroke, dictation chunk or shared text lands in storage at once.
  useEffect(() => {
    if (skipPersistRef.current) { skipPersistRef.current = false; return; }
    writeDraft(sessionId, input);
  }, [input, sessionId]);

  // A share from another app may carry words (a caption, a link): they land
  // in the composer once, after anything already typed.
  useEffect(() => {
    if (!sharedText) return;
    const text = takeSharedText();
    if (text) setInput((cur) => (cur ? `${cur.replace(/\s+$/, '')}\n${text}` : text));
  }, [sharedText, takeSharedText]);

  const submit = async () => {
    if (dictation.listening) dictation.stop();
    const text = input;
    if ((!text.trim() && attachments.length === 0) || thinking) return;
    setInput('');
    const ok = await send(text);
    if (!ok && text.trim()) setInput(text); // give the words back to fix and resend
    inputRef.current?.focus();
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="milli-console-chat">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4" data-testid="milli-messages">
        {messages.length === 0 && historyLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="font-mono text-[11px] tracking-[0.2em] text-stone-500 animate-pulse">
              {t('milli.historyLoading')}
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-md px-4">
              <div className="font-mono tracking-[0.2em] text-sm mb-2 text-[color:var(--milli-accent)]">MILLI</div>
              <p className="text-sm text-stone-400">{t('milli.emptyHint')}</p>
              <p className="mt-3 text-[11px] font-mono text-stone-600">{t('milli.emptyExamples')}</p>
            </div>
          </div>
        ) : (
          messages.map((m, i) => {
            if (m.role === 'user') {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-lg bg-[#2b2d2f] px-3.5 py-2.5 text-sm whitespace-pre-wrap">
                    {m.atts?.length ? (
                      <div className="mb-1.5 flex flex-wrap gap-1.5">
                        {m.atts.map((name, j) => (
                          <span key={j} className="inline-flex items-center gap-1 rounded border border-[#3a332a] bg-[#17181a] px-1.5 py-0.5 font-mono text-[10px] text-[color:var(--milli-accent)]">
                            <Paperclip size={10} />{name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {m.text}
                  </div>
                </div>
              );
            }
            if (m.role === 'error') {
              return (
                <div key={i} className="flex">
                  <div className="max-w-[85%] rounded-lg border border-red-500/40 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-300 whitespace-pre-wrap">
                    {m.text}
                  </div>
                </div>
              );
            }
            const hex = m.department?.hex;
            return (
              <div key={i} className="flex">
                <div className="max-w-[85%]">
                  <div className="font-mono text-[10px] tracking-[0.2em] mb-1" style={{ color: hex ?? 'var(--milli-accent)' }}>
                    MILLI{m.department ? ` · ${m.department.name.toUpperCase()}` : ''}
                  </div>
                  <div
                    className="rounded-lg border border-[#3a332a] bg-[#17181a] px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed"
                    style={hex ? { borderLeft: `3px solid ${hex}` } : undefined}
                  >
                    {m.text}
                  </div>
                </div>
              </div>
            );
          })
        )}
        {thinking ? (
          <div className="flex items-center gap-2 font-mono text-[11px] text-[color:var(--milli-accent)]" data-testid="milli-thinking">
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--milli-accent)] animate-ping" />
            {t('milli.thinking')}
          </div>
        ) : null}
      </div>

      {/* Composer */}
      <div className="border-t border-[#3a332a] bg-[#151618]">
        {attachments.length || attaching || dictation.interim ? (
          <div className="px-3 pt-2 flex flex-wrap gap-1.5" data-testid="milli-pending-attachments">
            {attachments.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded border border-[#3a332a] bg-[#17181a] px-2 py-1 font-mono text-[10px] text-[color:var(--milli-accent)]">
                <Paperclip size={10} />{a.name}
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="text-stone-500 hover:text-red-400"
                  aria-label="Remove attachment"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            {attaching ? (
              <span className="inline-flex items-center gap-1.5 rounded border border-[#3a332a] bg-[#17181a] px-2 py-1 font-mono text-[10px] text-stone-400 animate-pulse">
                <Paperclip size={10} />{t('milli.console.readingFile')}
              </span>
            ) : null}
            {dictation.interim ? (
              <span className="basis-full text-[12px] italic text-stone-500" data-testid="milli-dictation-interim">
                {dictation.interim}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="p-3 flex gap-2">
          <input ref={fileRef} type="file" accept={ATTACHMENT_ACCEPT} multiple hidden onChange={pickFiles} />
          <button
            type="button"
            onClick={() => {
              armPicker();
              fileRef.current?.click();
            }}
            disabled={thinking || attachments.length >= MAX_ATTACHMENTS}
            className="rounded-md border border-[#3a332a] text-stone-400 px-3 hover:text-[color:var(--milli-accent)] hover:border-[color:var(--milli-accent)] disabled:opacity-40 transition-colors"
            data-testid="milli-attach"
            aria-label={t('milli.console.attach')}
            title={t('milli.console.attach')}
          >
            <Paperclip size={16} />
          </button>
          {dictation.supported ? (
            <button
              type="button"
              onClick={dictation.toggle}
              disabled={thinking}
              className={`rounded-md border px-3 transition-colors disabled:opacity-40 ${
                dictation.listening
                  ? 'border-red-500/60 bg-red-500/15 text-red-300 animate-pulse'
                  : 'border-[#3a332a] text-stone-400 hover:text-[color:var(--milli-accent)] hover:border-[color:var(--milli-accent)]'
              }`}
              data-testid="milli-dictate"
              aria-pressed={dictation.listening}
              aria-label={dictation.listening ? t('milli.console.dictateStop') : t('milli.console.dictate')}
              title={dictation.listening ? t('milli.console.dictateStop') : t('milli.console.dictate')}
            >
              {dictation.listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          ) : null}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            placeholder={dictation.listening ? t('milli.console.listening') : t('milli.inputPlaceholder')}
            className="flex-1 resize-none rounded-md bg-[#17181a] border border-[#3a332a] px-3 py-2.5 text-sm text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-[color:var(--milli-accent)]"
            data-testid="milli-input"
          />
          <button
            type="button"
            onClick={submit}
            disabled={thinking || (!input.trim() && attachments.length === 0)}
            className="rounded-md border border-[color:var(--milli-accent)] text-[color:var(--milli-accent)] px-4 hover:bg-[#c9873e]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="milli-send"
            aria-label={t('milli.send')}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
